import React, { useContext, useLayoutEffect, useState } from 'react'
import {
  FancyModal,
  Avatar,
  FancyContent,
  FancyShowcase,
  FullPageLoader,
  CircleLoader
} from '@deal/components'
import Path from '#src/components/Path'
import { AnalyticsContext } from '#src/containers/Analytics'
import { useStartExpertRatingPathMutation, ExpertForRatingComponent } from '#src/generated/graphql'
import { IdentityContext } from '#src/containers/Identity'
import ExpertReferralModal from '#src/components/ExpertReferralModal'
import styles from './styles.css'
import { PathLayouts } from '#src/typings'

interface ExpertRatingProps {
  isOpen: boolean
  onCloseModal: () => void
  expertStarRating?: number
  expertId: string
  entryPoint: Tracking.EntryPoint
}
enum Modal {
  Rating,
  Referral
}

const ExpertRatingModal: React.SFC<ExpertRatingProps> = ({
  onCloseModal,
  isOpen,
  expertStarRating,
  expertId,
  entryPoint
}) => {
  const { myself } = useContext(IdentityContext)
  const analytics = useContext(AnalyticsContext)
  const [modal, setModal] = useState(Modal.Rating)
  const [flowId, setFlowId] = useState<undefined | string>(undefined)
  const [pageSlug, setPageSlug] = useState<undefined | string>(undefined)
  const [pathSlug, setPathSlug] = useState<undefined | string>(undefined)

  const [startExpertRatingPath, { loading }] = useStartExpertRatingPathMutation({
    variables: {
      input: {
        expertId,
        expertStartRating: expertStarRating
      }
    },
    onCompleted: ({ startExpertRatingPath: { flow, errors } }) => {
      if (flow) {
        const firstPage = flow.pagesConnection.edges[0].node

        setFlowId(flow.id)
        setPageSlug(firstPage.slug)
        setPathSlug(flow.slug)

        if (analytics) {
          /**
           * The expert rating path is created ahead of time so we can tap
           * into some template variables we wouldn't otherwise have access
           * to within the scehema. Long term this makes a case for obfuscating
           * these events to a provider, but this is the only place outside
           * of the Path component that we do this.
           */
          analytics.events.pathFlowCreated({
            path_flow_id: flow.id,
            source_key: 'expert-rating-modal',
            path_slug: flow.slug,
            experiment_variation: flow.slug,
            path_intent: 'expert-review'
          })

          analytics.events.pathPageViewed({
            path_flow_id: flow.id || '',
            path_slug: flow.slug || '',
            page_slug: firstPage.slug || ''
          })
        }
      } else {
        throw new Error(
          errors ? errors.join(', ').toString() : 'Error starting expert profile rating flow'
        )
      }
    }
  })

  useLayoutEffect(() => {
    if (isOpen) {
      startExpertRatingPath()
    }
  }, [isOpen])

  // Can't rate someone if you're not logged in
  if (!myself) {
    return null
  }

  return (
    <>
      <FancyModal
        isOpen={isOpen && modal === Modal.Rating}
        onRequestClose={onCloseModal}
        modalContentClassName={styles.modalContent}
      >
        <ExpertForRatingComponent variables={{ vanityIdOrId: expertId }}>
          {({ data }) => {
            if (data && data.findExpert) {
              const businessUser = data.findExpert
              const expertPhoto = businessUser.expertProfile?.profilePhoto
              return (
                <>
                  <FancyShowcase className={styles.expertShowcase}>
                    {expertPhoto && (
                      <Avatar
                        name={businessUser.displayName}
                        size={160}
                        src={expertPhoto.url}
                        className={styles.expertAvatar}
                      />
                    )}

                    <div className={styles.expertDetails}>
                      <div className={styles.expertName}>{businessUser.shortName}</div>
                      {businessUser.expertProfile && (
                        <div className={styles.expertHeadline}>
                          {businessUser.expertProfile.headline}
                        </div>
                      )}
                    </div>
                  </FancyShowcase>
                  <FancyContent className={styles.details}>
                    {loading ? (
                      <FullPageLoader loader={CircleLoader} />
                    ) : pathSlug && pageSlug ? (
                      <>
                        <Path
                          sourceKey="expert-rating-modal"
                          pathIntent="expert-review"
                          pathLayout={PathLayouts.MODAL}
                          pathSlug={pathSlug}
                          pageSlug={pageSlug}
                          flowId={flowId}
                          className={styles.pathContainer}
                          prefillData={expertStarRating ? { expertStarRating } : {}}
                          onPathComplete={() => setModal(Modal.Referral)}
                        />
                      </>
                    ) : null}
                  </FancyContent>
                </>
              )
            } else {
              return <FullPageLoader loader={CircleLoader} />
            }
          }}
        </ExpertForRatingComponent>
      </FancyModal>

      {/* Must not render modal if closed to avoid firing tracking event on mount */}
      {isOpen && modal === Modal.Referral && (
        <ExpertReferralModal
          onRequestClose={onCloseModal}
          entryPoint={entryPoint}
          businessUserId={expertId}
        />
      )}
    </>
  )
}

export default ExpertRatingModal
